import store from "../store";
import {
  IConfiguration,
  IConfigurationRegistry
} from "./configuration";
import { ConfigurationRegistry } from "../infrastructure/configuration";
import { ConfigurationRepository as Ver2ConfigurationRepository } from "../infrastructure/configuration/ver2";
import { IAuthenticationRegistry } from "./authentication";
import { AuthenticationRegistry as Ver2AuthenticationRegistry } from "../infrastructure/authentication/ver2";
import { IDepartmentRegistry } from "./department";
import { DepartmentRegistry as Ver2DepartmentRegistry } from "../infrastructure/department/ver2";
import {
  IDocumentRegistry,
  IFixedPhraseRegistry
} from "./document";
import {
  DocumentRegistry as Ver2DocumentRegistry,
  FixedPhraseRegistry as Ver2FixedPhraseRegistry
} from "../infrastructure/document/ver2";
import { IFormatRegistry } from "./format";
import { FormatRegistry as Ver2FormatRegistry } from "../infrastructure/format/ver2";
import { IPatientRegistry } from "./patient";
import { PatientRegistry as Ver2PatientRegistry } from "../infrastructure/patient/ver2";
import { IUserRegistry, User } from "./user";
import { UserRegistry as Ver2UserRegistry } from "../infrastructure/user/ver2";
import { IClaioRegistry } from "./claio";
import { IOcojoRegistry } from "./ocojo";
import { ClaioRegistry as Ver2ClaioRegistry } from "../infrastructure/claio/ver2";
import { OcojoRegistry as Ver2OcojoRegistry } from "../infrastructure/ocojo/ver2";
import { IHisRegistry } from "./his";
import { HisRegistry as Ver2HisRegistry } from "../infrastructure/his/ver2";
import { ILocalhostRegistry } from "./localhost";
import { LocalhostRegistry as Ver2LocalhostRegistry } from "../infrastructure/localhost/ver2";
import { ISystemRegistry } from "./system";
import { SystemRegistry } from "../infrastructure/system";
import { IContactRegistry } from "./contact";
import { ContactRegistry as Ver2ContactRegistry } from "../infrastructure/contact/ver2";
import { IWorkflowRegistry } from "./workflow";
import { WorkflowRegistry as Ver2WorkflowRegistry } from "../infrastructure/workflow/ver2";
import { IInstitutionRegistry } from "./institution";
import { InstitutionRegistry as Ver2InstitutionRegistry } from "../infrastructure/institution/ver2";
import { ICollaborationRegistry } from "./collaboration";
import { CollaborationRegistry as Ver2CollaborationRegistry } from "../infrastructure/collaboration/ver2";
import { IDeveloperRegistry } from "./developer";
import { DeveloperRegistry as Ver2DeveloperRegistry } from "../infrastructure/developer/ver2";
import { IExternalImageRegistry } from "./externalImage";
import { ExternalImageRegistry as Ver2ExternalImageRegistry } from "../infrastructure/externalImage/ver2";
import { IProjectTeamRegistry } from "./projectTeam";
import { ProjectTeamRegistry as Ver2ProjectTeamRegistry } from "../infrastructure/projectTeam/ver2";
import { IHelpRegistry } from "./help";
import { HelpRegistry as Ver2HelpRegistry } from "../infrastructure/help/ver2";
import { IExternalSystemRegistry } from "./externalSystem";
import { ExternalSystemRegistry } from "../infrastructure/externalSystem/ver2";
import { IHolidayRegistry } from "./holiday";
import { HolidayRegistry } from "../infrastructure/holiday/ver2";
import { AccountRegistry } from "../infrastructure/account/ver2";
import { IAccountRegistry } from "./account";

export class DomainRegistry<TConfiguration extends IConfiguration = IConfiguration> {
  // シングルトンインスタンス.
  static readonly instance: DomainRegistry = new DomainRegistry();

  constructor() {
    const configurationRepository = new Ver2ConfigurationRepository<TConfiguration>();
    this._configuration = new ConfigurationRegistry(configurationRepository);
    this.authentication = new Ver2AuthenticationRegistry();
    this.department = new Ver2DepartmentRegistry(this.getToken);
    this.document = new Ver2DocumentRegistry(this.getConfiguration, this.getToken);
    this.fixedPhrase = new Ver2FixedPhraseRegistry(this.getToken);
    this.format = new Ver2FormatRegistry(this.getToken);
    this.patient = new Ver2PatientRegistry(this.getToken);
    this.user = new Ver2UserRegistry(this.getConfiguration, this.getUser, this.getToken);
    this.claio = new Ver2ClaioRegistry(this.getToken);
    this.ocojo = new Ver2OcojoRegistry(this.getToken);
    this.his = new Ver2HisRegistry(this.getToken);
    this.externalImage = new Ver2ExternalImageRegistry(this.getToken);
    this.externalSystem = new ExternalSystemRegistry(this.getToken);
    this.localhost = new Ver2LocalhostRegistry(this.getConfiguration, this.getToken, this.claio, this.ocojo, this.externalImage, this.externalSystem);
    this.system = new SystemRegistry(this.getConfiguration, this.getToken, this.localhost);
    this.contact = new Ver2ContactRegistry(this.getToken);
    this.workflow = new Ver2WorkflowRegistry(this.getToken);
    this.institution = new Ver2InstitutionRegistry(this.getToken);
    this.collaboration = new Ver2CollaborationRegistry(this.getToken);
    this.developer = new Ver2DeveloperRegistry(this.getToken);
    this.projectTeam = new Ver2ProjectTeamRegistry(this.getToken);
    this.help = new Ver2HelpRegistry(this.getToken);
    this.holiday = new HolidayRegistry(this.getToken);
    this.account = new AccountRegistry(this.getToken);
  }

  public get configuration(): IConfigurationRegistry<TConfiguration> {
    return this._configuration;
  }
  protected _configuration: IConfigurationRegistry<TConfiguration>;
  public readonly authentication: IAuthenticationRegistry;
  public readonly department: IDepartmentRegistry;
  public readonly document: IDocumentRegistry;
  public readonly fixedPhrase: IFixedPhraseRegistry;
  public readonly format: IFormatRegistry;
  public readonly patient: IPatientRegistry;
  public readonly user: IUserRegistry;
  public readonly claio: IClaioRegistry;
  public readonly ocojo: IOcojoRegistry;
  public readonly his: IHisRegistry;
  public readonly externalImage: IExternalImageRegistry;
  public readonly localhost: ILocalhostRegistry;
  public readonly system: ISystemRegistry;
  public readonly contact: IContactRegistry;
  public readonly workflow: IWorkflowRegistry;
  public readonly institution: IInstitutionRegistry;
  public readonly collaboration: ICollaborationRegistry;
  public readonly developer: IDeveloperRegistry;
  public readonly projectTeam: IProjectTeamRegistry;
  public readonly help: IHelpRegistry;
  public readonly externalSystem: IExternalSystemRegistry;
  public readonly holiday: IHolidayRegistry;
  public readonly account: IAccountRegistry;

  protected getToken: () => string = () => store.getters.token;
  protected getUser: () => User = () => store.getters.user;
  protected getConfiguration: () => TConfiguration = () => store.getters.configuration;

  // リファクタ前のstatic実装.
  // TODO: リファクタしてinstance経由にしたら削除.
  static get configuration(): IConfigurationRegistry {
    return DomainRegistry.instance.configuration;
  }
  static get authentication(): IAuthenticationRegistry {
    return DomainRegistry.instance.authentication;
  }
  static get department(): IDepartmentRegistry {
    return DomainRegistry.instance.department;
  }
  static get document(): IDocumentRegistry {
    return DomainRegistry.instance.document;
  }
  static get fixedPhrase(): IFixedPhraseRegistry {
    return DomainRegistry.instance.fixedPhrase;
  }
  static get format(): IFormatRegistry {
    return DomainRegistry.instance.format;
  }
  static get patient(): IPatientRegistry {
    return DomainRegistry.instance.patient;
  }
  static get user(): IUserRegistry {
    return DomainRegistry.instance.user;
  }
  static get claio(): IClaioRegistry {
    return DomainRegistry.instance.claio;
  }
  static get ocojo(): IOcojoRegistry {
    return DomainRegistry.instance.ocojo;
  }
  static get his(): IHisRegistry {
    return DomainRegistry.instance.his;
  }
  static get externalImage(): IExternalImageRegistry {
    return DomainRegistry.instance.externalImage;
  }
  static get localhost(): ILocalhostRegistry {
    return DomainRegistry.instance.localhost;
  }
  static get system(): ISystemRegistry {
    return DomainRegistry.instance.system;
  }
  static get contact(): IContactRegistry {
    return DomainRegistry.instance.contact;
  }
  static get workflow(): IWorkflowRegistry {
    return DomainRegistry.instance.workflow;
  }
  static get institution(): IInstitutionRegistry {
    return DomainRegistry.instance.institution;
  }
  static get collaboration(): ICollaborationRegistry {
    return DomainRegistry.instance.collaboration;
  }
  static get developer(): IDeveloperRegistry {
    return DomainRegistry.instance.developer;
  }
  static get projectTeam(): IProjectTeamRegistry {
    return DomainRegistry.instance.projectTeam;
  }
  static get help(): IHelpRegistry {
    return DomainRegistry.instance.help;
  }
  static get externalSystem(): IExternalSystemRegistry {
    return DomainRegistry.instance.externalSystem;
  }
  static get holiday(): IHolidayRegistry {
    return DomainRegistry.instance.holiday;
  }
  static get account(): IAccountRegistry {
    return DomainRegistry.instance.account;
  }
}
